import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { CgProfile } from 'react-icons/cg';
import Select from 'react-select';
import paises from '../constants/Countries';
import WarningPopup from './WarningPopup';
import { useState, useRef } from 'react';
import Popup from 'reactjs-popup';
import { BsQuestionCircleFill } from 'react-icons/bs';
import pasaporteImage from '../assets/images/pasaporte.jpg';
import '../styles/RutPage.css';

const validationSchema = Yup.object({
  documento: Yup.string().required("Campo requerido"),
  rut: Yup.string().when("documento", (documento: any, schema) => {
    if (documento[0] == "cedula") {
      return schema
        .matches(
          /^\d{1,2}\d{3}\d{3}[-][0-9kK]{1}$/,
          { message: 'El RUT debe seguir el formato XXXXXXXX-X' }
        )
        .required("El RUT es requerido");
    } else {
      return schema
        .matches(/^[A-Z0-9]+$/, "Número de pasaporte inválido")
        .required("El número de pasaporte es requerido");
    }
  }),
  nacionalidad: Yup.string().when("documento", (documento: any, schema) => {
    if(documento[0] == "pasaporte")
      return schema.required("Campo requerido");
    else
      return schema;
  }),
});

// https://gist.github.com/donpandix/f1d638c3a1a908be02d5
const Fn = {
  // Valida el rut con su cadena completa "XXXXXXXX-X"
  validaRut(rutCompleto: string) {
    if (!/^[0-9]+[-|‐]{1}[0-9kK]{1}$/.test(rutCompleto)) return false;
    const tmp: Array<string> = rutCompleto.split('-');
    let digv: string | number = Number(tmp[1]) ? Number(tmp[1]) : tmp[1];
    const rut = Number(tmp[0]);
    if (digv === 'K') digv = 'k';
    return (Fn.dv(rut) === String(digv));
  },
  dv(rut: number) {
    let M = 2;
    let R = rut;
    let suma = 0;
    while (R !== 0) {
      const number = R % 10;
      suma += number * M; // 0 - 7

      R = Math.floor(R / 10);
      M += 1;

      if (M === 8) {
        M = 2;
      }
    }

    const ret = 11 - (suma % 11);
    if (ret === 11) {
      return '0';
    } if (ret === 10) {
      return 'k';
    }
    return String(ret);
  },
};

const colourStyles = {
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: 'white',
    border: state.isFocused ? 0 : 0,
    // This line disable the blue border
    boxShadow: state.isFocused ? 0 : 0,
    '&:hover': {
      border: state.isFocused ? 0 : 0,
    },
    fontWeight: 'lighter',
    fontSize: '14px',
  }),
  menu: (base: any, state: any) => ({
    ...base,
    backgroundColor: 'white',
    border: state.isFocused ? 0 : 0,
    // This line disable the blue border
    boxShadow: state.isFocused ? 0 : 0,
    '&:hover': {
      border: state.isFocused ? 0 : 0,
    },
    fontWeight: 'lighter',
    fontSize: '14px',
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#80808060',
    fontWeight: 'lighter',
    fontSize: '14px',
  }),

};

const RutContainer = ({
  setRutFromThis,
}: {
  setRutFromThis: (rut: string, documentType: string, nationality: string) => void;
}) => {
  const [showPassportModal, setShowPassportModal] = useState<boolean>(false);
  const [showPassportHelpModal, setShowPassportHelpModal] = useState<boolean>(false);
  const setFieldValueRef = useRef<any>(null);
  const [selectedDocument, setSelectedDocument] = useState({ value: 'cedula', label: 'Cédula de identidad' });

  const PassportHelpButton = () => (
    <button
      type="button"
      onClick={() => setShowPassportHelpModal(true)}
      style={{
        boxShadow: '0 2px 8px rgba(90,181,66,0.10)',
        background: 'white',
        width: '30px',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        marginLeft: '12px',
        padding: '0',
        margin: '4',
      }}
    >
      <BsQuestionCircleFill size={22} color="#5ab542" />
    </button>
  );

  const PassportHelpModal = () => (
    <Popup
      open={showPassportHelpModal}
      modal
      closeOnDocumentClick={false}
      contentStyle={{
        background: 'none',
        border: 'none',
        padding: '0',
        width: 'fit-content',
        maxWidth: '500px',
        overflow: 'hidden'
      }}
      overlayStyle={{
        background: 'rgba(0, 0, 0, 0.5)',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '20px',
          borderRadius: '10px',
          width: '90%',
          maxWidth: '600px',
          textAlign: 'center',
          position: 'relative',
          margin: '0 auto',
          overflow: 'hidden'
        }}
      >
        <button
          onClick={() => setShowPassportHelpModal(false)}
          style={{
            position: 'absolute',
            top: '10px',
            right: '15px',
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#999',
            padding: '0',
            width: 'auto'
          }}
        >
          ×
        </button>

        <h3 style={{
          color: '#5ab542',
          marginBottom: '15px',
          fontSize: '18px'
        }}>
          Documento de identidad extranjera
        </h3>

        <p style={{
          color: '#666',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          Ejemplo de documento válido:
        </p>

        <img
          src={pasaporteImage}
          alt="Ejemplo de pasaporte o cédula extranjera"
          style={{
            maxWidth: '100%',
            height: '300px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        />

        <p style={{
          color: '#666',
          marginTop: '15px',
          fontSize: '12px',
          fontStyle: 'italic'
        }}>
          Ingresa el número resaltado en amarillo
        </p>
      </div>
    </Popup>
  );

  return (
    <div className="mainContainerRutPage">
      <p className="containerTitle">
        <CgProfile style={{ marginRight: '4%' }} size={45} />
        Datos de paciente
      </p>
      <p className="containerSubTitle">Ingrese datos del paciente que será atendido</p>
      <div className="inputContainer">
        <Formik
          initialValues={{
            documento: 'cedula',
            rut: '',
            nacionalidad: '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setFieldError }) => {
            if (values.documento === 'cedula' && !Fn.validaRut(values.rut)) {
              setFieldError('rut', 'El RUT ingresado no es válido');
              return;
            }
            setRutFromThis(values.rut, values.documento, values.nacionalidad);
          }}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({
            errors, touched, setFieldValue, values,
          }) => {
            setFieldValueRef.current = setFieldValue;
            return (
              <Form>
                <label className="labelForm">Documento de identificación</label>
                <Select
                  value={selectedDocument}
                  placeholder="Selecciona un documento"
                  className="formSelector"
                  styles={colourStyles}
                  options={[
                    { value: 'cedula', label: 'Cédula de identidad' },
                    { value: 'pasaporte', label: 'Pasaporte / Cédula extranjera' },
                  ]}
                  onChange={(documento: any) => {
                    setSelectedDocument(documento);
                    setFieldValue('documento', documento.value);
                    if (documento.value === 'pasaporte') {
                      setShowPassportModal(true);
                    }
                  }}
                />
                {values.documento === 'pasaporte' && (
                  <>
                    <label className="labelForm">Nacionalidad</label>
                    <div
                      data-event="change_nacionalidad"
                      data-value={values.nacionalidad || ''}
                      style={{ display: 'contents' }}
                    >
                      <Select
                        placeholder="Selecciona una nacionalidad"
                        className="formInput"
                        styles={colourStyles}
                        options={paises.map((item: any) => ({ value: item, label: item }))}
                        onChange={(nacionalidad: any) => {
                          setFieldValue('nacionalidad', nacionalidad.value);


                        }}
                      />
                    </div>
                    {errors.nacionalidad && touched.nacionalidad ? (
                      <div className="error">{String(errors.nacionalidad)}</div>
                    ) : null}
                  </>
                )}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <label className="labelForm">
                    {values.documento === 'cedula' ? 'RUT' : 'DNI (N° cédula identidad extranjera)'}
                  </label>
                  {values.documento === 'pasaporte' && <PassportHelpButton />}
                </div>
                <Field
                  id="rut"
                  name="rut"
                  placeholder={
                    values.documento === "cedula"
                      ? "Ingrese RUT del paciente"
                      : "Ingrese número de cédula sin puntos ni guion"
                  }
                  className="fieldForm"
                  onChange={(e: any) => {
                    setFieldValue('rut', e.target.value);
                  }}
                />
                <p className="rutDescription">{values.documento === 'cedula' ? '*Ejemplo 12345678-9' : ''}</p>
                {errors.rut && touched.rut
                  ? (<div className="error">{String(errors.rut)}</div>)
                  : null}
                <button
                  className="rutSearchButton"
                  type="submit"

                  data-event="click_btn_buscar_identificacion"
                  data-value=""
                >
                  Continuar
                </button>
              </Form>
            );
          }}
        </Formik>

        <WarningPopup
          isOpen={showPassportModal}
          onClose={() => {
            setShowPassportModal(false);
            setFieldValueRef.current('documento', 'cedula');
            setSelectedDocument({ value: 'cedula', label: 'Cédula de identidad' });
          }}
          onContinue={() => {
            setShowPassportModal(false);
          }}
          title="Importante"
          message="Si eres extranjero y tienes RUT nacional, ingresa con tu RUT nacional. Si no lo tienes, puedes ingresar con tu pasaporte."
          subMessage=""
          continueText="Entiendo, continuar"
          cancelText="Volver"
          titleColor="#cc0000"
        />

        <PassportHelpModal />
      </div>
    </div>
  );
};

export default RutContainer;
